import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Disc, ChevronDown } from 'lucide-react';
import { projects, pickLang } from '../mock';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';


const VideoModal = ({ video, onClose }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    setVisible(false);
    setTimeout(onClose, 300);
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center"
      onClick={close}
    >
      <div
        className={`relative transform transition-all duration-300 ease-out ${
          visible ? "scale-100 opacity-100" : "scale-0 opacity-0"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-4 border-ember rounded-lg shadow-[0_0_20px_rgba(214, 155, 93,0.7)] bg-black p-2">
          <video
            src={video.src}
            autoPlay
            controls
            playsInline
            className="max-w-[80vw] max-h-[80vh] object-contain"
          />
        </div>

        <button
          className="absolute -top-4 -right-4 text-white text-3xl font-bold"
          onClick={close}
        >
          ✕
        </button>
      </div>
    </div>,
    document.body
  );
};

/* ================================
   BOUTON D'EXTRAIT — hero, direction B ("un extrait mis en avant")
   Les extraits auto-hebergés (mp4) s'ouvrent toujours dans la modale
   du site. Les extraits Instagram, eux, s'ouvrent directement sur
   Instagram dans un nouvel onglet plutot que dans un embed en modale :
   le lecteur Instagram est pense pour du format vertical (Reels),
   pas pour la modale actuelle (pensee pour du mp4 en 16:9) — et
   depend d'un script externe (embed.js) qui peut mettre du temps a
   charger ou rester bloque sur l'apercu generique "Voir sur
   Instagram". Ouvrir un nouvel onglet donne le vrai lecteur natif,
   sans dependance ni decalage de mise en page.
================================= */
const VideoTrigger = ({ video, primary, onOpen }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || 'fr';
  const isInstagram = video.type === 'instagram';
  // Le Reel est repere par son type ('instagram'), pas par le texte de
  // son label : celui-ci est desormais {fr, en} et sa traduction ne
  // doit pas casser cette detection.
  const label = primary && isInstagram ? t('projectDetail.hero.view_reel') : pickLang(video.label, lang);

  const className = primary
    ? 'btn-primary animate-fadeIn'
    : 'inline-flex items-center gap-2 border border-ember/40 text-paper font-mono text-[10.5px] uppercase tracking-[0.1em] px-4 py-2 rounded hover:border-ember hover:bg-ember/10 hover:text-amber transition-colors animate-fadeIn';

  const icon = (
    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none">
      <polygon points="5,3 19,12 5,21" fill={primary ? 'var(--color-ink-950)' : 'currentColor'} />
    </svg>
  );

  if (isInstagram) {
    return (
      <a href={video.src} target="_blank" rel="noopener noreferrer" className={className}>
        {icon}
        {label}
      </a>
    );
  }

  return (
    <button className={className} onClick={() => onOpen(video)}>
      {icon}
      {label}
    </button>
  );
};



export const ProjectDetail = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || 'fr';
  const categoryLabels = {
    Ads: t('portfolio.filter.ads'),
    Fiction: t('portfolio.filter.fiction'),
    Animation: t('portfolio.filter.animation'),
  };

  // Indicateur de scroll (fleche qui s'estompe) : anime via ref/DOM direct
  // plutot que via un state React (voir l'effet plus bas) pour ne pas
  // re-rendre tout le composant a chaque pixel defile.
  const scrollIndicatorRef = useRef(null);



  const { id } = useParams();
  const project = projects.find(p => p.id === id);

  const [activeVideo, setActiveVideo] = useState(null);

  const normalizedVideos = React.useMemo(() => project?.videos || [], [project]);

  // Lightbox states
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);

  // "Plus d'images" repliée par défaut (direction L3)
  const [showMoreImages, setShowMoreImages] = useState(false);

  /* ================================
     BLOCAGE SCROLL (lightbox ouverte)
  ================================= */
  useEffect(() => {
    document.body.style.overflow = showLightbox ? 'hidden' : '';
  }, [showLightbox]);

  /* ================================
     SCROLL TOP + FADE OBSERVER
  ================================= */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle('animate-fadeIn', entry.intersectionRatio > 0.1);
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.fade-element, .gallery-item');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [id]);


  useEffect(() => {
  const max = 160; // distance avant disparition
  const onScroll = () => {
    const progress = Math.min(window.scrollY / max, 1);
    const el = scrollIndicatorRef.current;
    if (el) {
      el.style.opacity = 1 - progress;
      el.style.transform = `translate(-50%, ${progress * 40}px)`;
    }
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  return () => window.removeEventListener('scroll', onScroll);
}, []);

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white bg-ink-800">
        {t('projectDetail.not_found')}
      </div>
    );
  }

  /* ================================
     GALERIE — masonry CSS (columns)
     Ancien système : positions "top/left/width" en % codées en dur par
     image, avec la hauteur du conteneur DEVINÉE à partir de la largeur
     (aucune vraie mesure). Ca ne pouvait pas marcher pour toutes les
     photos a la fois : un multiplicateur assez haut pour ne pas
     chevaucher les portraits serrés (ex. Aya) laissait un vide énorme
     (~600px) sous les images plus larges/courtes (ex. Editing con) —
     et inversement. Remplacé par un vrai masonry en colonnes CSS : le
     navigateur calcule la vraie hauteur de chaque image, donc plus
     aucune estimation, plus de vide ni de chevauchement possible.
  ================================= */
  const galleryImages = project.images?.slice(1) || [];

  /* ================================
     CTA HERO — un extrait mis en avant (direction B)
     Plutot que N boutons identiques sans hierarchie (l'oeil ne sait
     pas lequel regarder en premier), un seul devient le vrai bouton
     d'appel : le Reel s'il existe, sinon le premier extrait. Les
     autres passent en pastilles secondaires, plus discretes.
  ================================= */
  const primaryVideo = normalizedVideos.find((v) => v.type === 'instagram') || normalizedVideos[0];
  const secondaryVideos = normalizedVideos.filter((v) => v !== primaryVideo);

  /* ================================
     LIGHTBOX IMAGES
  ================================= */
  const Lightbox = ({ images, startIndex, onClose }) => {
  const [index, setIndex] = useState(startIndex);

  const prev = (e) => {
    e.stopPropagation();
    setIndex((index - 1 + images.length) % images.length);
  };
  const next = (e) => {
    e.stopPropagation();
    setIndex((index + 1) % images.length);
  };

  // Si image est un objet, prendre .src
  const getImageSrc = (img) => (typeof img === 'string' ? img : img.src);

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/80 flex justify-center items-center p-4 cursor-pointer"
      onClick={onClose}
    >
      <img
        src={getImageSrc(images[index])}
        alt={`${t('projectDetail.gallery.alt_prefix')} ${index + 1}`}
        className="max-w-full max-h-full object-contain"
        onClick={(e) => e.stopPropagation()}
      />
      <button className="absolute top-6 right-6 text-white text-3xl" onClick={onClose}>
        ✕
      </button>
      <button className="absolute left-6 top-1/2 transform -translate-y-1/2 text-white text-4xl" onClick={prev}>
        ‹
      </button>
      <button className="absolute right-6 top-1/2 transform -translate-y-1/2 text-white text-4xl" onClick={next}>
        ›
      </button>
    </div>,
    document.body
  );
};



  // BreakdownVideo modal
const BreakdownVideo = ({ src, onClose }) => {
  const videoRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    videoRef.current?.play();
    const timeout = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(timeout);
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 400);
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center"
      onClick={handleClose}
    >
      <div
        className={`relative w-auto h-auto max-w-[90vw] max-h-[90vh] flex items-center justify-center
          transform transition-all duration-400 ease-in-out
          ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-0'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-4 border-ember rounded-lg shadow-[0_0_20px_rgba(214, 155, 93,0.7)] overflow-hidden max-w-full max-h-full">
          <video
            ref={videoRef}
            src={src}
            controls
            autoPlay
            loop={false}
            muted
            playsInline
            className="w-auto h-auto max-w-full max-h-[90vh] object-contain"
          />
        </div>

        <button
          className="absolute top-4 right-4 text-white text-3xl font-bold"
          onClick={handleClose}
        >
          ✕
        </button>
      </div>
    </div>,
    document.body
  );
};

const [showBreakdown, setShowBreakdown] = useState(false);

  /* ================================
     RENDER
  ================================= */
  return (
    <div className="relative bg-ink-950 bg-textured text-paper">
      {/* HERO */}
      <div className="relative w-full h-screen overflow-hidden hero-image flex justify-center items-center">
        <img src={project.thumbnail} alt={pickLang(project.title, lang)} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/50" />
        <div className="absolute inset-0 flex flex-col justify-center items-center text-center px-6 z-20">
          <h1 className="font-display text-5xl md:text-7xl uppercase mb-4">{pickLang(project.title, lang)}</h1>
          <p className="font-body text-gray-300 max-w-2xl">{pickLang(project.description, lang)}</p>
          {normalizedVideos.length > 0 && (
  <div className="flex flex-col items-center gap-4 mt-8">
    <VideoTrigger video={primaryVideo} primary onOpen={setActiveVideo} />
    {secondaryVideos.length > 0 && (
      <div className="flex gap-2 justify-center flex-wrap">
        {secondaryVideos.map((video, i) => (
          <VideoTrigger key={i} video={video} onOpen={setActiveVideo} />
        ))}
      </div>
    )}
  </div>
)}

{/* Scroll indicator */}
<div
  ref={scrollIndicatorRef}
  className="absolute bottom-10 left-1/2 -translate-x-1/2 pointer-events-none"
  style={{
    opacity: 1,
    transform: 'translate(-50%, 0px)',
    transition: 'opacity 0.1s linear, transform 0.1s linear'
  }}
>



  <svg
    className="scroll-arrow"
    width="72"
    height="72"
    viewBox="0 0 24 24"
    fill="none"
  >
    <path
      d="M12 4v16M12 20l-7-7M12 20l7-7"
      stroke="var(--color-ember)"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
</div>



        </div>
      </div>

      {/* GALERIE — bande horizontale défilante, hauteur plafonnée (56vh) :
          la page ne s'allonge plus avec le nombre de photos (contrairement
          à l'empilement plein-écran précédent, où les photos portrait
          s'affichaient à leur hauteur naturelle et gonflaient la page).
          Chaque photo garde son ratio propre (largeur auto), juste
          recadrée à la même hauteur. Rien n'est caché : on glisse
          horizontalement au lieu de scroller verticalement. */}
      <div className="container mx-auto px-6" style={{ marginTop: 32 }}>
        <div className="gallery-strip flex gap-3 overflow-x-auto pb-4">
          {galleryImages.map((image, i) => {
            const imgSrc = typeof image === 'string' ? image : image.src;
            return (
              <div
                key={i}
                className="gallery-item cursor-pointer flex-none rounded overflow-hidden"
                style={{ height: '64vh' }}
                onClick={() => { setLightboxIndex(i); setShowLightbox(true); }}
              >
                <img
                  src={imgSrc}
                  alt=""
                  className="h-full w-auto object-cover block transition-transform duration-300 ease-out hover:scale-105"
                />
              </div>
            );
          })}
        </div>
        {galleryImages.length > 0 && (
          <div className="meta-label text-center mt-3">{t('projectDetail.sections.scroll_hint')}</div>
        )}
      </div>


   {/* WRAPPER GLOBAL DU BAS DE PAGE — début */}
<div className="relative w-full">

{activeVideo && (
  <VideoModal
    video={activeVideo}
    onClose={() => setActiveVideo(null)}
  />
)}





{/* CONTENU APRÈS LE DECK AVEC PATTERN STYLE "FEATURED PROJECTS" */}
<section className="relative py-20 overflow-hidden">
{/* BACKGROUND GLOBAL UNIFIÉ */}
<div className="absolute inset-0 z-0 pointer-events-none">
  {/* Gradient principal — sobre, pas de particules générées aléatoirement
      à chaque rendu (coûteux et trop chargé pour la nouvelle direction) */}
  <div
    className="absolute inset-0"
    style={{
      background: 'linear-gradient(135deg, rgba(11,13,14,0.97) 0%, rgba(22,24,26,0.92) 100%)',
    }}
  />
  <div
    className="absolute inset-0"
    style={{
      backgroundImage: 'repeating-linear-gradient(45deg, rgba(214,155,93,0.04) 0 1px, transparent 1px 28px)',
    }}
  />
</div>
{/* Bouton retour */}
<div className="self-start mb-0 ml-12">
  <Link
    to="/portfolio"
    className="inline-flex items-center gap-3 font-mono text-xs tracking-widest uppercase text-ember hover:text-amber transition-colors fade-element"
  >
    <ArrowLeft size={18} /> {t('projectDetail.back_to_portfolio')}
  </Link>
</div>



    
  {/* Contenu au-dessus du pattern */}
  <div className="container mx-auto px-6 relative z-10">


{/* === INFOS DU PROJET ===
    Fusionne l'ancien bandeau "glass" (Rôle/Client/Année) et les cartes
    "Le projet / Process / Intention / Note" en une seule section
    éditoriale : fond photo du projet assombri + halo ember + grain +
    repères d'angle façon viseur, un titre, une ligne de métadonnées,
    et le texte du projet fusionné en un seul paragraphe qui coule. */}
<div className="relative overflow-hidden rounded-xl border border-white/10 my-16 fade-element">
  <div className="absolute inset-0 z-0">
    <img src={project.thumbnail} alt="" className="w-full h-full object-cover opacity-20 saturate-50" />
    <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(11,13,14,0.72), rgba(11,13,14,0.93) 55%, var(--color-ink-950) 100%)' }} />
  </div>
  <div
    className="absolute left-1/2 -top-[8%] w-3/4 h-[55%] -translate-x-1/2 pointer-events-none z-[1]"
    style={{ background: 'radial-gradient(ellipse 60% 60% at 50% 30%, rgba(214,155,93,0.18), transparent 70%)' }}
  />
  <div
    className="absolute pointer-events-none z-[2]"
    style={{ inset: '-30%', backgroundImage: 'var(--grain-svg)', backgroundSize: '130px 130px', opacity: 0.06, mixBlendMode: 'overlay' }}
  />
  <div className="absolute top-5 left-5 w-5 h-5 border-t border-l border-ember/50 z-[5]"></div>
  <div className="absolute top-5 right-5 w-5 h-5 border-t border-r border-ember/50 z-[5]"></div>
  <div className="absolute bottom-5 left-5 w-5 h-5 border-b border-l border-ember/50 z-[5]"></div>
  <div className="absolute bottom-5 right-5 w-5 h-5 border-b border-r border-ember/50 z-[5]"></div>

  <div className="relative z-10 px-8 md:px-12 py-12 md:py-14">
    <div className="eyebrow mb-5">{t('projectDetail.sections.project')}</div>
    <h2 className="font-display text-2xl md:text-3xl uppercase mb-5">
      {pickLang(project.title, lang)} — {categoryLabels[project.meta?.category] || project.meta?.category}
      {project.meta?.year ? `, ${project.meta.year}` : ''}
    </h2>

    <div className="flex flex-wrap gap-x-6 gap-y-2 font-mono text-[11.5px] uppercase tracking-wide text-gray-400 mb-6 pb-5 border-b border-white/10">
      {[
        { label: t('projectDetail.hero.role_label'), value: pickLang(project.meta?.role, lang) },
        { label: t('projectDetail.hero.client_label'), value: project.meta?.client },
        { label: t('projectDetail.hero.year_label'), value: project.meta?.year },
      ].filter((item) => item.value).map((item, i) => (
        <div key={i}>
          <span className="text-amber">{item.label}</span>{' '}
          <span className="text-paper normal-case tracking-normal">{item.value}</span>
        </div>
      ))}
    </div>

    <p className="font-body text-paper text-base md:text-lg leading-relaxed max-w-3xl">
      {pickLang(project.content.summary, lang)
        || [project.content.about, project.content.intent, project.content.process]
          .map((f) => pickLang(f, lang))
          .filter(Boolean)
          .join(' ')}
    </p>

    {project.content.notes && (
      <p className="font-body text-gray-400 italic text-sm mt-6 max-w-2xl border-l-2 border-ember pl-4">
        {pickLang(project.content.notes, lang)}
      </p>
    )}
  </div>
</div>



  </div>

{/* === SECTION BREAKDOWN + PLUS D'IMAGES ===
    Les deux se partagent desormais un seul conteneur (celui du
    breakdown) : "Plus d'images" n'a plus son propre conteneur, il
    vient simplement se caler en bas de celui-ci. La section entiere
    reste facultative sur breakdownVideo OU deckImages (un projet peut
    avoir l'un sans l'autre, ex. deckImages seul). */}
{(project.breakdownVideo || (project.deckImages && project.deckImages.length > 0)) && (
  <section className="project-breakdown relative pt-12 pb-20 text-center">
    <div className="max-w-3xl mx-auto px-6">
      {project.breakdownVideo && (
        <>
          <h2 className="font-display text-3xl uppercase text-ember mb-4">{t('projectDetail.sections.breakdown_title')}</h2>
          <p className="font-body text-gray-300 mb-8">{t('projectDetail.sections.breakdown_subtitle')}</p>

          <button
            className="btn-primary"
            onClick={() => setShowBreakdown(true)}
          >
            {/* Triangle play SVG */}
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none">
              <polygon points="5,3 19,12 5,21" fill="var(--color-ink-950)" />
            </svg>
            {t('projectDetail.sections.breakdown_play')}
          </button>
        </>
      )}

      {project.deckImages && project.deckImages.length > 0 && (
        <div className={project.breakdownVideo ? 'mt-16 text-left' : 'text-left'}>
          <button
            onClick={() => setShowMoreImages((v) => !v)}
            className="w-full flex items-center justify-between gap-4 px-5 py-4 border border-ember/40 rounded bg-gradient-to-r from-ember/5 to-transparent hover:from-ember/10 transition-colors cursor-pointer text-left fade-element"
          >
            <div className="flex items-center gap-3">
              <Disc className="text-ember flex-shrink-0" size={22} />
              <span className="font-display text-lg uppercase tracking-wide">{t('projectDetail.sections.more_images')}</span>
              <span className="font-mono text-[11px] text-gray-400">{t('projectDetail.sections.photos_count', { count: project.deckImages.length })}</span>
            </div>
            <ChevronDown
              className="text-ember transition-transform duration-300 flex-shrink-0"
              style={{ transform: showMoreImages ? 'rotate(180deg)' : 'none' }}
              size={20}
            />
          </button>
          <div
            className="overflow-hidden transition-all duration-500 ease-out"
            style={{ maxHeight: showMoreImages ? '1400px' : '0px', marginTop: showMoreImages ? 20 : 0 }}
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-2">
              {project.deckImages.map((img, i) => (
                <div
                  key={i}
                  className="group relative aspect-[3/4] overflow-hidden border border-white/10 shadow-[0_0_0_1px_transparent] transition-all duration-300 hover:rounded-2xl hover:shadow-[0_0_0_1px_rgba(214,155,93,0.6)]"
                >
                  <img
                    src={img}
                    alt=""
                    className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>

    {showBreakdown && (
      <BreakdownVideo src={project.breakdownVideo} onClose={() => setShowBreakdown(false)} />
    )}
  </section>
)}

  {/* LIGHTBOX */}
  {showLightbox && (
    <Lightbox
      images={project.images.slice(1)}
      startIndex={lightboxIndex}
      onClose={() => setShowLightbox(false)}
    />
  )}
</section>

</div> {/* FIN WRAPPER GLOBAL DU BAS DE PAGE */}

</div>

  );
};
